import * as React from 'react';
import { fetchItemShippingEstimate, isShippingEstimateLoading } from '@/redux/modules/shipping';
import { FormattedMessage, useIntl } from 'react-intl';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import { ShippingLineText, StyledActionText, textStyles } from './helpers/styles';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import CountriesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/CountriesWithISOCodes';
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import isEqual from 'react-fast-compare';
import { isUserLoggedIn } from '@/redux/modules/user';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';

const cookie = new Cookies();
const expires = new Date();
expires.setDate(expires.getDate() + 60);

type Props = {
    countryCode: string;
    itemId: number;
    setShippingToLocation: Function;
    setShowShippingToLocation: Function;
    zip: string;
};

type ErrorFeedbackProps = {
    spacerMode?: boolean;
};

type CountryOptionsProps = {
    currCountryCode: string;
};

/**
 * Updates shipping location for calculating shipping cost.
 */
const ZipcodeForm = ({ countryCode, itemId, setShippingToLocation, setShowShippingToLocation, zip }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const [hasFormError, setHasFormError] = React.useState(false);
    const [canSubmit, setCanSubmit] = React.useState(Boolean(zip));
    const [currentZip, setCurrentZip] = React.useState(zip);
    const userLoggedIn = useSelector(isUserLoggedIn);
    const shippingEstimateLoading = useSelector((state) => isShippingEstimateLoading(state, itemId));

    const compareCountryCode = postcodeValidatorExistsForCountry(countryCode) ? countryCode : 'US';

    const validateAndSetState = (z: string, cc: string) => {
        const valid = postcodeValidator(z, cc);
        setCanSubmit(valid);
        setCurrentZip(z);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!postcodeValidator(currentZip, compareCountryCode as string)) {
            setHasFormError(true);
            return;
        } else {
            setShippingToLocation((state) => ({ ...state, zip: currentZip }));
            if (!userLoggedIn) {
                cookie.set('bidder-zip', currentZip, {
                    domain: '.liveauctioneers.com',
                    expires,
                });
            } else {
                cookie.remove('bidder-zip');
            }
        }
        // @ts-ignore
        dispatch(fetchItemShippingEstimate({ countryCode, itemId, postalCode: currentZip })).then((data) => {
            // There was an error getting the shipping estimate
            if (data?.error) {
                return;
            }

            // Reset any previous form errors
            if (hasFormError) {
                setHasFormError(false);
            }

            // Hide form
            setShowShippingToLocation();
        });
    };

    const CountryOptions = ({ currCountryCode }: CountryOptionsProps) => (
        <>
            {CountriesWithISOCodes.map((country) => {
                country.code = country.code.replace('GB', 'UK');
                return (
                    <option key={country.name} value={country.code}>
                        {/* //TODO: fix this to use the country name country.name */}
                        {currCountryCode === country.code ? currCountryCode : country.name}
                    </option>
                );
            })}
        </>
    );

    const ErrorFeedback = ({ spacerMode }: ErrorFeedbackProps) => (
        <ErrorFeedbackContainer>
            <Text colorError mini>
                {spacerMode ? (
                    <span>&nbsp;</span>
                ) : (
                    <FormattedMessage
                        id={
                            countryCode === 'US'
                                ? 'shippingInfo.shippingEstimateForm.zipInputErrorUS'
                                : 'shippingInfo.shippingEstimateForm.zipInputErrorNonUS'
                        }
                    />
                )}
            </Text>
        </ErrorFeedbackContainer>
    );

    return (
        <>
            <ShippingLineText secondary>
                <FormattedMessage
                    id={
                        countryCode === 'US'
                            ? 'shippingInfo.shippingEstimateForm.enterZipUS'
                            : 'shippingInfo.shippingEstimateForm.enterZipNonUS'
                    }
                />
            </ShippingLineText>
            <StyledForm onSubmit={handleSubmit}>
                <FormFieldsContainer>
                    <FormFields>
                        <FormLabel htmlFor="zipcode">
                            <ShippingLineText>
                                <FormattedMessage id="shippingInfo.shippingEstimateForm.zipInputLabel" />
                            </ShippingLineText>
                        </FormLabel>
                        <StyledInput
                            hideSpacer
                            name="countryCode"
                            onChange={(e) => {
                                const newCountryCode = (e.target as HTMLInputElement).value;
                                setShippingToLocation((state) => ({ ...state, countryCode: newCountryCode }));
                            }}
                            type="select"
                            value={countryCode}
                        >
                            <CountryOptions currCountryCode={countryCode} />
                        </StyledInput>
                        <StyledInput
                            $notInUS={countryCode !== 'US'}
                            hideSpacer
                            name="zipcode"
                            onChange={(e) => {
                                const newZip = (e.target as HTMLInputElement).value || '';
                                validateAndSetState(newZip, compareCountryCode);
                            }}
                            placeholder={formatMessage({
                                id:
                                    countryCode === 'US'
                                        ? 'shippingInfo.shippingEstimateForm.zipInputPlaceholderUS'
                                        : 'shippingInfo.shippingEstimateForm.zipInputPlaceholderNonUS',
                            })}
                            valid={!hasFormError}
                            // value={zip}
                        />
                    </FormFields>
                    {hasFormError && <ErrorFeedback />}
                </FormFieldsContainer>

                <span>
                    <StyledActionText disabled={!canSubmit}>
                        {shippingEstimateLoading ? (
                            <StyledIcon icon={faSpinner} spin />
                        ) : (
                            <FormattedMessage id="shippingInfo.shippingEstimateForm.applyZipButton" />
                        )}
                    </StyledActionText>
                    {hasFormError && <ErrorFeedback spacerMode />}
                </span>
            </StyledForm>
        </>
    );
};

export default React.memo<Props>(ZipcodeForm, isEqual);

const StyledForm = styled.form`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const ErrorFeedbackContainer = styled.span`
    display: flex;
    justify-content: flex-end;
    margin-top: 5px;
    margin-right: 10px;
`;

const FormFieldsContainer = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`;

const FormFields = styled.span`
    display: flex;
    flex-direction: row;
    align-items: center;

    > * {
        margin-right: 10px;
    }
`;

const FormLabel = styled.label`
    margin-bottom: unset;
    white-space: nowrap;
`;

const StyledInput = styled(Input).attrs(() => ({
    inputClassName: 'inputClassName',
}))<{ $notInUS?: boolean }>`
    &&& {
        .inputClassName {
            ${textStyles}
            text-align: center;
            height: 30px;
            &::placeholder {
                font-family: ${({ theme }) => theme.typography.fontSecondaryLight};
                letter-spacing: 0.5px;
            }

            ${(props) =>
                props?.valid === false &&
                css`
                    padding-left: 0;
                    padding-right: 20px;
                `}
            background-position: right 6px center !important;
        }
    }

    max-width: ${({ $notInUS }) => ($notInUS ? '135px' : '99px')};
`;

const StyledIcon = styled(FontAwesomeIcon)`
    color: ${({ theme }) => theme.colors.grey100};
`;
